/**
 * Cookie-consent banner + preferences manager.
 *
 * - Shows once until the visitor accepts, rejects, or saves preferences.
 * - Decision is persisted to localStorage. Legacy key `wofy_cookie_consent`
 *   is still written (values: "accepted" | "rejected" | "custom") for any
 *   existing analytics gates. Detailed choices live under
 *   `wofy_cookie_prefs`.
 * - Fires window CustomEvent `wofy-cookie-consent` with {choice, prefs}
 *   so future analytics/marketing scripts can gate themselves on the
 *   visitor's granular choice.
 * - On IndianFamilyTree.com the banner is DPDP-aligned: three CTAs
 *   (Accept All / Manage Preferences / Reject Non-Essential) and copy
 *   that names the Digital Personal Data Protection Act, 2023.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";
import { resolveSiteSlug } from "@/config/siteConfig";

const STORAGE_KEY = "wofy_cookie_consent";
const PREFS_KEY = "wofy_cookie_prefs";

// Essential is always on — the site cannot function without login /
// session / security cookies. The other three are opt-in.
const DEFAULT_PREFS = {
  essential: true,
  personalization: false,
  analytics: false,
  features: false,
};

const CATEGORIES = [
  {
    key: "essential",
    label: "Strictly Necessary",
    hint: "Login, session, security and fraud prevention. Always on.",
    locked: true,
  },
  {
    key: "personalization",
    label: "Personalization",
    hint: "Remembers language, family tree settings, and display preferences.",
  },
  {
    key: "analytics",
    label: "Analytics & Performance",
    hint: "Aggregated data about which features are used most and how the site performs.",
  },
  {
    key: "features",
    label: "AI & Feature Enhancement",
    hint: "Tailored suggestions, saved AI preferences, and improved user interactions.",
  },
];

function readPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed, essential: true };
  } catch { return null; }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  // Detect IndianFamilyTree.com so we can show DPDP-aligned copy and
  // link to the IFT-specific Privacy Policy + Cookie Policy + Consent
  // policy from the banner.
  const isIft = useMemo(() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      const slug = resolveSiteSlug({ queryParam: q || "" });
      return (slug || "").toLowerCase().includes("indianfamilytree");
    } catch { return false; }
  }, []);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      const savedPrefs = readPrefs();
      if (savedPrefs) setPrefs(savedPrefs);
      if (!existing) setVisible(true);
    } catch { /* localStorage unavailable — silently skip */ }
  }, []);

  const persist = (choice, prefsChosen) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefsChosen));
    } catch {}
    try {
      window.dispatchEvent(
        new CustomEvent("wofy-cookie-consent", { detail: { choice, prefs: prefsChosen } }),
      );
    } catch {}
    setVisible(false);
    setManaging(false);
  };

  const acceptAll = () => {
    const all = { essential: true, personalization: true, analytics: true, features: true };
    setPrefs(all);
    persist("accepted", all);
  };
  const rejectNonEssential = () => {
    const only = { essential: true, personalization: false, analytics: false, features: false };
    setPrefs(only);
    persist("rejected", only);
  };
  const saveCustom = () => {
    const next = { ...prefs, essential: true };
    persist("custom", next);
  };

  if (!visible) return null;

  const cookiePolicySlug = isIft ? "ift-cookie-policy" : "cookie-policy";
  const privacyPolicySlug = isIft ? "ift-privacy-policy" : "privacy-policy";

  return (
    <>
      {/* Compact banner (bottom-right) */}
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        data-testid="cookie-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-30 max-w-md rounded-2xl border shadow-2xl"
        style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-full grid place-items-center shrink-0"
              style={{ background: "var(--wofy-surface)" }}
            >
              <Cookie className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            </div>
            <div className="flex-1">
              <div className="overline mb-1">
                {isIft ? "DPDP · COOKIE PREFERENCES" : "COOKIE PREFERENCES"}
              </div>
              <h3 className="font-display text-lg leading-tight">
                {isIft ? "A note about cookies & your data." : "A small note about cookies."}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                {isIft ? (
                  <>
                    We use cookies to improve your experience, secure your account, analyze usage, and
                    personalize features. IndianFamilyTree.com processes personal data in accordance with
                    India's <b>Digital Personal Data Protection Act, 2023</b>. You can manage your
                    preferences at any time.
                  </>
                ) : (
                  <>
                    We use essential cookies to keep you signed in and secure. With your permission, we may also
                    use analytics + functional cookies to improve the site. You can change your mind any time.
                  </>
                )}
              </p>
              <div className="mt-2 text-xs">
                Read the full{" "}
                <Link to={`/policies/${cookiePolicySlug}`} onClick={() => setVisible(false)} className="underline" data-testid="cookie-banner-policy-link">
                  Cookie Policy
                </Link>{" "}
                and{" "}
                <Link
                  to={`/policies/${privacyPolicySlug}`}
                  onClick={() => setVisible(false)}
                  className="underline"
                  data-testid="cookie-banner-privacy-link"
                >
                  Privacy Policy
                </Link>
                {isIft && (
                  <>
                    {" "}·{" "}
                    <Link
                      to="/policies/ift-consent-policy"
                      onClick={() => setVisible(false)}
                      className="underline"
                      data-testid="cookie-banner-consent-link"
                    >
                      Consent & Authorization
                    </Link>
                  </>
                )}
                .
              </div>
            </div>
            <button
              data-testid="cookie-banner-dismiss"
              onClick={rejectNonEssential}
              aria-label="Dismiss"
              className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              data-testid="cookie-banner-manage"
              onClick={() => setManaging(true)}
              className="wofy-btn-ghost text-sm inline-flex items-center gap-1"
            >
              <Settings2 className="w-3.5 h-3.5" /> Manage preferences
            </button>
            <button
              data-testid="cookie-banner-reject"
              onClick={rejectNonEssential}
              className="wofy-btn-ghost text-sm"
            >
              Reject non-essential
            </button>
            <button
              data-testid="cookie-banner-accept"
              onClick={acceptAll}
              className="wofy-btn-primary text-sm"
            >
              Accept all cookies
            </button>
          </div>
        </div>
      </div>

      {/* Manage-preferences modal */}
      {managing && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Manage cookie preferences"
          data-testid="cookie-prefs-modal"
        >
          <div
            className="w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden"
            style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
          >
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--wofy-border)" }}>
              <div>
                <div className="overline">COOKIE PREFERENCES</div>
                <h3 className="font-display text-xl mt-1">Choose what you allow</h3>
              </div>
              <button
                onClick={() => setManaging(false)}
                aria-label="Close"
                data-testid="cookie-prefs-close"
                className="opacity-60 hover:opacity-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {CATEGORIES.map((c) => (
                <label
                  key={c.key}
                  className="flex items-start justify-between gap-4 p-3 rounded-xl border"
                  style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                  data-testid={`cookie-pref-row-${c.key}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}{c.locked && <span className="ml-2 text-[10px] font-mono uppercase opacity-60">Always on</span>}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{c.hint}</div>
                  </div>
                  <input
                    type="checkbox"
                    disabled={c.locked}
                    checked={!!prefs[c.key]}
                    onChange={(e) => setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))}
                    data-testid={`cookie-pref-${c.key}`}
                    className="mt-1 h-5 w-5 accent-current"
                  />
                </label>
              ))}
            </div>
            <div className="p-4 border-t flex flex-wrap justify-end gap-2" style={{ borderColor: "var(--wofy-border)" }}>
              <button
                data-testid="cookie-prefs-reject"
                onClick={rejectNonEssential}
                className="wofy-btn-ghost text-sm"
              >
                Reject non-essential
              </button>
              <button
                data-testid="cookie-prefs-save"
                onClick={saveCustom}
                className="wofy-btn-ghost text-sm"
              >
                Save selection
              </button>
              <button
                data-testid="cookie-prefs-accept-all"
                onClick={acceptAll}
                className="wofy-btn-primary text-sm"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
